import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';

import { externalCalibrationService, serialNumbersService } from 'services';
import { ROUTES } from 'constants/routes';
import { authorizedPdfList } from 'constants/authorizedPdfList';
import { ExternalCalibration, ServiceStation } from 'interfaces';
import { CustomInput, CustomButton, Spinner } from 'components';
import {
  authorizedValidationSchemeAgent,
  authorizedValidationSchemeCustomer,
} from 'validation';

import './EditAuthorizedPdfForm.scss';
import { useAppSelector } from '../../hooks';
import axios from 'axios';
import { AnyObject, TestContext } from 'yup';
import { CustomValidity } from '../CustomValidity';

export const EditAuthorizedPdfForm = () => {
  const { certId } = useParams();
  const navigate = useNavigate();
  const [errMessage, setErrMessage] = useState<string>('');

  const [serviceStations, setServiceStations] = useState<[] | ServiceStation[]>(
    []
  );

  const [isUpdateRequestLoading, setIsUpdateRequestLoading] =
    useState<boolean>(false);
  const [isSuccessfullyUpdated, setIsSuccessfullyUpdated] =
    useState<boolean>(false);

  const { user } = useAppSelector((state) => state.auth);
  const [isPremium, setIsPremium] = useState<boolean>(false);

  const isNonAuthorizedUser = !user || user?.type === 'shipping company';

  const getFilteredFields = () => {
    return authorizedPdfList.filter((field) => {
      if (isNonAuthorizedUser)
        return [
          'sn',
          'computerSn',
          'imo',
          'vessel',
          'email',
          'produktionsdatum',
        ].includes(field.id);

      return !['email', 'produktionsdatum', 'gab-service-station'].includes(
        field.id
      );
    });
  };

  const [fields, setFields] = useState(getFilteredFields());

  useEffect(() => {
    setFields(getFilteredFields());
  }, [user]);

  const computerSnField = fields.find((o) => o.id === 'computerSn');

  const [initialValues, setInitialValues] = useState<ExternalCalibration>({
    sn: '',
    imo: '',
    computerSn: '',
    technician: '',
    certificateNr: '',

    cleanWaterValue: 0,
    firstCheckIntended: 0,
    firstCheckValue: 0,
    secondCheckIntended: 0,
    secondCheckValue: 0,

    vessel: '',
    batchCalCheck: '',
    placeOfCalibration: '',
    dateOfCalibration: '',
    serviceStationId: 0,

    separatorMarker: '',
    separatorInfo: '',
    deckmaInfo: '',

    fiveYearly: '',
    monthly: 12,

    produktionsdatum: '',
    email: '',
  });

  const snValidation = async (value: any, ctx: TestContext<AnyObject>) => {
    const sn = value.toString();

    const serial = await serialNumbersService.findSerial(sn);

    if (computerSnField) computerSnField.disabled = serial.typ !== 'Messzelle';

    return true;
  };

  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      setIsUpdateRequestLoading(true);
      setErrMessage('');

      try {
        if (!values.imo) delete values.imo;
        if (!values.vessel?.trim()) delete values.vessel;

        await externalCalibrationService.update(values);
        setIsUpdateRequestLoading(false);
        setIsSuccessfullyUpdated(true);

        navigate(
          `${ROUTES.documents_overview}/${values.sn}/${values.certificateNr}`
        );
      } catch (e) {
        setIsUpdateRequestLoading(false);
        setIsSuccessfullyUpdated(false);
        if (axios.isAxiosError(e) && e.response)
          setErrMessage(e.response.data?.message || 'Something went wrong');
      }
    },
    enableReinitialize: true,
    validationSchema: isNonAuthorizedUser
      ? authorizedValidationSchemeCustomer(
          snValidation,
          !computerSnField?.disabled || false
        )
      : authorizedValidationSchemeAgent(snValidation, false),
  });

  useEffect(() => {
    setIsSuccessfullyUpdated(false);

    (async () => {
      if (!certId) return;

      const response = await externalCalibrationService
        .findByCertificateNumber(certId)
        .catch(() => navigate(ROUTES.pdf_history));
      if (!response) return;

      setInitialValues({
        technician: response.technician,
        sn: response.sn,
        computerSn: response.computerSn || '',
        serviceStationId: response.serviceStation.id,
        imo: response.imo ? Number(response.imo) : '',
        batchCalCheck: response.batchCalCheck,
        certificateNr: response.certificateNr,
        dateOfCalibration: response.dateOfCalibration,
        placeOfCalibration: response.placeOfCalibration,
        vessel: response.vessel,

        cleanWaterValue: response.cleanWaterValue,
        firstCheckIntended: response.firstCheckIntended,
        firstCheckValue: response.firstCheckValue,
        secondCheckIntended: response.secondCheckIntended,
        secondCheckValue: response.secondCheckValue,

        separatorInfo: response.separatorInfo,
        separatorMarker: response.separatorMarker,
        deckmaInfo: response.deckmaInfo,

        monthly: response.fiveYearly ? '' : response.monthly,
        fiveYearly: response.fiveYearly,

        produktionsdatum: '',
        email: '',
      });
      setServiceStations([response.serviceStation]);
      setIsPremium(response.serviceStation.premium);
      await formik.setFieldValue(
        'serviceStationId',
        response.serviceStation.id
      );

      if (response.canEdit && !isNonAuthorizedUser)
        for (const field of fields) {
          field.disabled = false;
        }
      const serial = await serialNumbersService.findSerial(response.sn);
      if (computerSnField) {
        computerSnField.disabled = serial.typ !== 'Messzelle';
      }
    })();
  }, [certId, user, fields]);

  return (
    <div className="edit-pdf-container">
      <h1 className="edit-pdf__header">Edit calibration</h1>
      <form onSubmit={formik.handleSubmit}>
        <div className="edit-pdf-form-list">
          {!isNonAuthorizedUser && (
            <div className="edit-pdf__select-box">
              <p>Service Station</p>
              <select
                name="serviceStationId"
                id="serviceStationId"
                disabled
                className="edit-pdf__select"
                value={formik.values.serviceStationId}
                onChange={async (e) => {
                  const serviceStationId = Number(e.target.value);
                  await formik.setFieldValue(
                    'serviceStationId',
                    serviceStationId
                  );
                }}
                onBlur={formik.handleBlur}
              >
                {serviceStations.length &&
                  serviceStations.map((serviceStation) => (
                    <option value={serviceStation.id} key={serviceStation.id}>
                      {serviceStation.company}
                    </option>
                  ))}
              </select>
            </div>
          )}
          {fields.map(({ id, name, type, disabled }) => {
            const identifier = id as keyof ExternalCalibration;

            if (type === 'gab') return <div key={id}></div>;
            if (type === 'space')
              return <div key={id} className="generate-pdf__space"></div>;

            if (id === 'validity')
              return (
                <div key={identifier}>
                  <CustomValidity
                    label={name}
                    disabledFiveYearly={disabled}
                    disabledMonthly={
                      disabled || Boolean(formik.values['fiveYearly'])
                    }
                    width="100%"
                    showCheckbox={isPremium}
                    valueFiveYearly={formik.values['fiveYearly']}
                    valueMonthly={formik.values['monthly']}
                    onChangeFiveYearly={(e) => {
                      const fiveYearly = fields.find(
                        (o) => o.id === 'validity'
                      );
                      formik.handleChange(e);
                      if (fiveYearly) fiveYearly.disabled = e.target.checked;
                      if (e.target.checked) formik.setFieldValue('monthly', '');
                      else formik.setFieldValue('monthly', 12);
                    }}
                    onChangeMonthly={formik.handleChange}
                    onBlurMonthly={formik.handleBlur}
                    onBlurFiveYearly={formik.handleBlur}
                  />
                </div>
              );

            return (
              <div key={id}>
                <CustomInput
                  id={id}
                  name={id}
                  disabled={disabled}
                  value={formik.values[identifier]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label={name}
                  type={type}
                />
                {formik.touched[identifier] && formik.errors[identifier] && (
                  <div className="edit-pdf__error-validation-box">
                    <p className="edit-pdf__error-validation-text">
                      {formik.errors[identifier]}
                    </p>
                  </div>
                )}
                {identifier === 'sn' &&
                  formik.touched[identifier] &&
                  !formik.errors[identifier] && (
                    <div className="generate-pdf__success-validation-box">
                      <p className="generate-pdf__success-validation-text">
                        Serial number is valid
                      </p>
                    </div>
                  )}
                {identifier === 'produktionsdatum' && (
                  <div className="generate-pdf__info-box">
                    <p className="generate-pdf__info-text">
                      The productions date is to find on the type plate of the
                      Measuring Cell and written in the Format (mm/yy).
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {!isUpdateRequestLoading && (
          <div className="edit-pdf__button-box">
            <CustomButton type="submit" text="Update calibration" width="35%" />
          </div>
        )}
        {isUpdateRequestLoading && (
          <div className="edit-pdf__spinner-box">
            <Spinner />
          </div>
        )}
        {isSuccessfullyUpdated && (
          <div className="edit-pdf__success">PDF successfully updated</div>
        )}
        {errMessage && <div className="edit-pdf__error">{errMessage}</div>}
      </form>
    </div>
  );
};
